/**
 * The page's recent errors, kept in memory so a bug report can say what broke
 * (tech-spec-admin-bug-reports, Task 13). Nothing here is sent anywhere on its
 * own — the report dialog snapshots it when a draft is started, shows the admin
 * every entry, and lets them remove any before sending.
 *
 * Fed by the tRPC QueryCache/MutationCache `onError` hooks (the "button does
 * nothing" failures) and by window `error` / `unhandledrejection`.
 */
import type { ClientErrorEntry } from './types';

const MAX_ENTRIES = 20;
const WINDOW_MS = 15 * 60 * 1000;
const MAX_MESSAGE = 500;

let buffer: ClientErrorEntry[] = [];
let listenersInstalled = false;

export function recordClientError(entry: {
  kind: ClientErrorEntry['kind'];
  message: string;
  path?: string;
  code?: string;
}): void {
  buffer.push({
    at: new Date().toISOString(),
    kind: entry.kind,
    message: (entry.message ?? '').slice(0, MAX_MESSAGE),
    ...(entry.path && { path: entry.path }),
    ...(entry.code && { code: entry.code }),
  });
  if (buffer.length > MAX_ENTRIES) buffer = buffer.slice(-MAX_ENTRIES);
}

/**
 * A tRPC failure. A `BAD_REQUEST` keeps its path and code but DROPS its message:
 * zod's validation messages echo what the admin typed, which may be a child's
 * name, and the report's context goes to GitHub.
 */
export function recordTrpcError(error: unknown): void {
  const data =
    error && typeof error === 'object' && 'data' in error
      ? (error as { data?: { path?: unknown; code?: unknown } | null }).data
      : undefined;
  const code = typeof data?.code === 'string' ? data.code : undefined;
  const path = typeof data?.path === 'string' ? data.path : undefined;
  const message =
    code === 'BAD_REQUEST' ? '' : error instanceof Error ? error.message : String(error);
  recordClientError({ kind: 'trpc', message, path, code });
}

/** Errors from the last 15 minutes, oldest first, as copies. */
export function getRecentClientErrors(now: number = Date.now()): ClientErrorEntry[] {
  return buffer.filter((e) => now - Date.parse(e.at) <= WINDOW_MS).map((e) => ({ ...e }));
}

/** Idempotent — the report button calls it on every mount. */
export function installGlobalErrorListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  window.addEventListener('error', (event) => {
    const message =
      event.message || (event.error instanceof Error ? event.error.message : 'Script error');
    recordClientError({ kind: 'js', message });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    recordClientError({
      kind: 'promise',
      message: reason instanceof Error ? reason.message : String(reason),
    });
  });
}

export function __resetClientErrorBufferForTests(): void {
  buffer = [];
  listenersInstalled = false;
}
